import React, { useEffect, useState } from 'react';
import { Row, Col } from "react-bootstrap";
import { useParams } from "react-router-dom"; // Import useParams
import "../../style/EnterPin.css";
import trivialogo from "../../image/TriviaMaker-Logo.png";
import firebase from '../../firebase';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import landscapeIcon from '../../image/landscape-icon.webp';

const EnterPin = ({ onNavigate }) => {
  const { slug } = useParams(); // Extract the slug from the URL
  const [gameData, setGameData] = useState(null); // State to store fetched data
  const [students, setStudents] = useState([]); // Dynamic students array
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [modalOpen, setModalOpen] = useState(false); // For handling modal visibility
  const [selectedStudent, setSelectedStudent] = useState(null); // To store the clicked student data
  const [teamAssignments, setTeamAssignments] = useState([]); // Array to keep track of student-team assignments
  const [enteredPin, setEnteredPin] = useState(""); // Track entered PIN
  const [pinError, setPinError] = useState(""); // Track PIN validation error
  const [isLandscape, setIsLandscape] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const BootstrapTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.black,
    },
  }));

  const checkLandscapeMode = () => {
    const isMobile = /Mobi|Android/i.test(navigator.userAgent); // Check if the device is mobile
    if (isMobile && window.matchMedia("(orientation: landscape)").matches) {
      setIsLandscape(true);
      console.log("Landscape mode detected on mobile");
    } else {
      setIsLandscape(false);
      console.log("Not landscape mode or not mobile");
    }
  };

  useEffect(() => {
    checkLandscapeMode(); // Check on mount

    window.addEventListener('resize', checkLandscapeMode); // Check on resize

    return () => {
      window.removeEventListener('resize', checkLandscapeMode); // Clean up the event listener
    };
  }, []);

  const studentAdd = (student) => {
    setSelectedStudent(student);
    setModalOpen(true);
    setEnteredPin(""); // Clear previous PIN
    setPinError(""); // Clear previous error
  };

  const closeModal = () => {
    setModalOpen(false);
  };


  const fetchGameData = async () => {
    try {
      setLoading(true);

      // Fetch initial game data from Firebase
      const response = await fetch(`https://trivia-maker-tv.firebaseio.com/AppleTV/${slug}.json`);
      if (!response.ok) {
        throw new Error(`Error fetching data: ${response.statusText}`);
      }
      const data = await response.json();
      setGameData(data);

      // Listen for changes in the students data from Firebase Realtime Database
      const db = firebase.database();
      const ref = db.ref(`AppleTV/${slug}/students`);

      // Handle the initial fetch and subsequent updates
      ref.on('value', snapshot => {
        const students = snapshot.val();
        console.log("student", students);
        if (Array.isArray(students)) {
          const dynamicStudents = students.map((student, index) => {
            const parts = student.stuname.split(" - ");
            const color = parts[0].split(" ")[1] || "gray";
            const initials = parts[0].split(" ")[0] || "??";
            const name = parts[1] || "Unknown";
            const textColor = color.toLowerCase() === "white" ? "black" : "white";

            return {
              id: student.stuid || index,
              color,
              initials,
              name,
              textColor,
              correctAnswers: student.correctanswers || 0,
              gamesPlayed: student.gameplayed || 0,
              questionsAttempted: student.questionattempted || 0,
              email: student.stuemail || "N/A",
              stupin: student.stupin || "",
            };
          });

          setStudents(dynamicStudents);
        } else {
          console.error("Invalid or missing students data:", students);
          setStudents([]);
        }
      });

    } catch (err) {
      console.error("Error fetching game data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch game data on component mount
    fetchGameData();

    // Cleanup function to remove the Firebase listener when the component unmounts
    return () => {
      const db = firebase.database();
      const ref = db.ref(`AppleTV/${slug}/students`);
      ref.off(); // This will remove the listener
    };
  }, [slug]); // Ensure to re-run the effect if the `slug` changes

  const handlePinSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return; // Prevent duplicate submissions
    setIsSubmitting(true); // Set the state to indicate submission is in progress

    // Validate the length of the PIN
    if (enteredPin.length !== 4) {
      setPinError("You must enter exactly 4 digits!");
      setIsSubmitting(false);
      return;
    }

    if (selectedStudent && enteredPin === selectedStudent.stupin) {
      console.log("PIN validated successfully!");

      const initials = selectedStudent.initials || "Default Initials";
      const profileColor = selectedStudent.color || "gray";
      const name = selectedStudent.name || "No Name";

      if (!name) {
        setPinError("Invalid student name.");
        setIsSubmitting(false);
        return;
      }

      const db = firebase.database();
      const ref = db.ref(`AppleTV/${slug}`);

      try {
        const snapshot = await ref.once("value");
        const databaseData = snapshot.val() || {};
        let students = Array.isArray(databaseData.students) ? databaseData.students : [];
        let teamNames = Array.isArray(databaseData.teamNames) ? databaseData.teamNames : [];
        let teams = Array.isArray(databaseData.teams) ? databaseData.teams : [];

        // Start the transaction
        await ref.transaction((currentData) => {
          // Ensure currentData is initialized properly
          if (!currentData) {
            currentData = {
              teamNames: [],
              students: [],
              teams: []
            };
          }

          // Filter out the selected student
          const updatedStudents = currentData.students.filter(student => {
            const studentNameAfterHyphen = student.stuname.split(" - ")[1];
            return studentNameAfterHyphen !== selectedStudent.name;
          });

          currentData.students = updatedStudents;

          // Add the new team member
          const newTeamMember = {
            initials,
            name,
            profileColor,
          };

          // Ensure teamNames is initialized before pushing
          if (!Array.isArray(currentData.teamNames)) {
            currentData.teamNames = [];
          }

          currentData.teamNames.push(newTeamMember);

          // Assign the new team member to a team
          const teamIndex = currentData.teamNames.length - 1;
          const allocatedTeam = currentData.teams[teamIndex % currentData.teams.length] || "Default Team";
          newTeamMember.allocatedTeam = allocatedTeam;
          const team1bgColor = databaseData.team1bgColor || ""; // Default to an empty string
          const team2bgColor = databaseData.team2bgColor || ""; // Default to an empty string

          // Store team-related information in localStorage
          localStorage.setItem("initials", initials);
          localStorage.setItem("name", name);
          localStorage.setItem("profileColor", profileColor);
          localStorage.setItem("allocatedTeam", allocatedTeam);

          if (allocatedTeam === teams[0] && team1bgColor) {
            localStorage.setItem("allocatedTeamColor", team1bgColor);
          } else if (allocatedTeam === teams[1] && team2bgColor) {
            localStorage.setItem("allocatedTeamColor", team2bgColor);
          }

          return currentData;
        });

        console.log("Form submitted successfully with updated team names");
        onNavigate("AllocatedTeamToParticipant");
        setModalOpen(false);
      } catch (error) {
        console.error(error);
        setPinError("An error occurred. Please try again.");
      }
    } else {
      setPinError("Incorrect PIN. Please try again.");
    }
    setIsSubmitting(false);
  };




  if (loading) {
    return <div className="loading-message">Loading...</div>;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  return (
    <>
      {isLandscape ? (<>
        <div
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '1.5rem',
            fontWeight: 'bolder',
            backgroundColor: '#27738e',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            padding: '1.5vh'
          }}
        >
          <span className='mt-2 mb-4 p-3 '> To view this game, you will need to place your device in portrait mode</span>

          <div style={{ padding: '1.5vh' }}>
            <img src={landscapeIcon} alt="landscapeIcon" height={200} className="rotate-icon" />
          </div>
        </div>
      </>) : (

        <>
          <div className="leaderboardMain">
            <Row>
              <Col className="h-100 w-100 d-flex justify-content-center align-center EnterParticipantRow">
                <div>
                  <div className="mt-4 welcome-header">
                    <h3 className="welcome-message" id="classroomWelcomeMsg">
                      Welcome to our Classroom
                    </h3>
                    <div className="classroom-title my-2 mb-3 d-flex align-items-center">
                      <img
                        className="classroom-image"
                        src="https://triviamaker.com/wp-content/themes/stratusx-child/template/images/Classroom.png"
                        alt="Classroom"
                      />
                      <h4 className="classroom-name m-0 d-flex align-items-center" id="clsnameid">
                        &nbsp;{slug}
                      </h4>
                    </div>
                  </div>

                  <div className="user-display" id="userdisplayid">
                    <div className="py-0 py-lg-2 join-our-game">
                      <h4 className="m-0 mb-lg-1 join-game-msg" id="joinGameMsg">
                        Click your initials to join our game.
                      </h4>
                      <h5 className="m-0 initials-question" id="initialsQuestion">
                        Don't see your initials?
                      </h5>
                    </div>
                    <div className="usermaindiv">
                      <div className="p-2 user-main-div d-flex flex-wrap" id="studntsdivid" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {students.map((student) => (
                          <div
                            key={student.id}
                            className="student-container"
                            style={{ position: 'relative', opacity: 1 }}
                            onClick={() => studentAdd(student)}
                          >
                            <BootstrapTooltip title={student.name}>
                              <div
                                className="animal-circle active-user-border group-user"
                                style={{
                                  backgroundColor: student.color,
                                  color: student.textColor,
                                  cursor: 'pointer',
                                }}
                              >
                                {student.initials}
                              </div>
                            </BootstrapTooltip>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>



                  <div className="quiz-maker-banner">
                    <div className="upcoming-game-container mb-" style={{ height: "15%" }}>
                      <h4 className="upcoming-game-title" id="upcomingGameTitle">
                        Upcoming Game:
                      </h4>
                      <div className="upcomminggamediv d-flex" id="upcomminggamediv">
                        <img
                          className="game-icon"
                          src="https://triviamaker.com/wp-content/themes/stratusx-child/template/image/tic-tac-icon.png"
                          alt="Tic Tac Icon"
                        />
                        <h4 className="game-name m-0 d-flex align-items-center">
                          &nbsp;
                          {gameData?.gameName
                            ? gameData.gameName.length > 20
                              ? `${gameData.gameName.substring(0, 20)}...`
                              : gameData.gameName
                            : "No Game Data"}
                        </h4>

                      </div>
                      <div className="logo-div">
                        <img className="trivia-logo" src={trivialogo} alt="Trivia Maker Logo" />
                        <br />
                      </div>
                    </div>
                    <div>
                      Free{" "}
                      <b>
                        <a
                          href="https://triviamaker.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="quiz-maker-link"
                        >
                          Quiz Maker
                        </a>
                      </b>
                    </div>
                    <div>Create Fun Trivia Quizzes in Minutes</div>
                  </div>
                </div>
              </Col>
              {modalOpen && (
                <div className="modal-overlay">
                  <div className="modal-content-enterpin">
                    <span
                      style={{
                        position: 'absolute',
                        top: '6px',
                        right: '7px',
                        cursor: 'pointer',
                      }}
                      onClick={closeModal}
                    >
                      <img
                        style={{ width: '25px' }}
                        src="https://triviamaker.com/wp-content/themes/stratusx-child/template/images/close-popup.png"
                        alt="Close"
                      />
                    </span>

                    <form onSubmit={handlePinSubmit}>
                      <h4 style={{ textAlign: "center", fontSize: "32px" }}>Student Pin</h4>
                      {pinError && <span style={{ color: "red", textAlign: 'center', display: 'block' }}>{pinError}</span>}
                      <input
                        value={enteredPin}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) { // Allow only numeric input
                            setEnteredPin(value);
                          }
                        }}
                        maxlength="4"// Limit input to 4 digits
                        className="input-class"

                        placeholder="Enter Your pin"
                        style={{ margin: "16px auto", textAlign: "center" }}
                      />

                      <div className="btn-group d-flex justify-content-evenly mt-3">
                        <button
                          type="submit"
                          style={{
                            backgroundColor: "#27738e",
                            padding: "10px 30px",
                            borderRadius: "5px",
                            border: "none",
                            color: "white",
                          }}
                        >
                          Submit
                        </button>
                        <button
                          type="submit"
                          style={{
                            backgroundColor: "#d5d5d5",
                            padding: "10px 30px",
                            borderRadius: "5px",
                            border: "none",
                            color: "black",
                          }}
                          onClick={closeModal}
                        >
                          close
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </Row>
          </div>
        </>
      )}

    </>
  );
};

export default EnterPin;
